/**
 * The game page's own log for a report (ADR-081's amendment of 2026-10-05, item 4): a ring of the newest lines the
 * page wrote with `console.log`, `console.warn` and `console.error`, each clipped, kept from the moment the page's
 * script starts so the boot and the join are in it. The wrapped functions always call the originals, so the
 * browser's console shows everything as before. The gateway redacts credentials from what is sent; the page logs
 * no token, session id, name or chat line (the ADR lists what it logs).
 */

/**
 * How many lines the ring holds.
 */
export const CONSOLE_RING_LINES = 200;

/**
 * The most characters one line keeps.
 */
export const CONSOLE_LINE_CHARS = 500;

/**
 * The console levels the ring records.
 */
const LEVELS = ["log", "warn", "error"] as const;

/**
 * The part of the console the capture replaces.
 */
export type ConsoleLike = Record<(typeof LEVELS)[number], (...args: unknown[]) => void>;

/**
 * A bounded record of lines; a push past the capacity drops the oldest.
 */
export interface ConsoleRing {
  /**
   * Appends one line, clipped to `CONSOLE_LINE_CHARS`.
   *
   * @param line The line.
   */
  readonly push: (line: string) => void;
  /**
   * @returns The held lines joined by line feeds, oldest first; empty when nothing was recorded.
   */
  readonly text: () => string;
}

/**
 * Makes a ring.
 *
 * @param capacity How many lines it holds, at least one.
 * @returns The ring.
 */
export function createConsoleRing(capacity: number = CONSOLE_RING_LINES): ConsoleRing {
  const size = Math.max(1, Math.floor(capacity));
  const slots: string[] = [];
  let next = 0;
  let filled = false;
  return {
    push: (line) => {
      slots[next] = line.length > CONSOLE_LINE_CHARS ? line.slice(0, CONSOLE_LINE_CHARS) : line;
      next += 1;
      if (next === size) {
        next = 0;
        filled = true;
      }
    },
    text: () => (filled ? [...slots.slice(next), ...slots.slice(0, next)] : slots.slice(0, next)).join("\n"),
  };
}

/**
 * One console argument as text on one line: a string as it is, an error as its stack, anything else as JSON where
 * it serialises.
 *
 * @param value The argument.
 * @returns The text, its line breaks replaced by spaces.
 */
function wordsOf(value: unknown): string {
  let words: string;
  if (typeof value === "string") {
    words = value;
  } else if (value instanceof Error) {
    words = value.stack ?? `${value.name}: ${value.message}`;
  } else {
    try {
      words = typeof value === "object" && value !== null ? (JSON.stringify(value) ?? String(value)) : String(value);
    } catch {
      words = String(value);
    }
  }
  return words.replace(/\s*[\r\n]+\s*/g, " ");
}

/**
 * The arguments of a console call without a `%c` styling: each `%c` leaves the format string and the style argument
 * that answers it, which a log read by a person has no use for.
 *
 * @param args The call's arguments.
 * @returns The arguments to record.
 */
function unstyled(args: readonly unknown[]): readonly unknown[] {
  const [format, ...rest] = args;
  if (typeof format !== "string" || !/%c/.test(format.replace(/%%/g, ""))) {
    return args;
  }
  const styles = (format.match(/%[%c]/g) ?? []).filter((mark) => mark === "%c").length;
  return [format.replace(/%[%c]/g, (mark) => (mark === "%c" ? "" : mark)), ...rest.slice(styles)];
}

/**
 * Records a console's lines in a ring from now on, each as `<time> <level> <words>`.
 *
 * @param target The console.
 * @param ring Where the lines go.
 * @param now Writes the moment a line was logged.
 * @returns A function that puts the console's own functions back.
 */
export function captureConsole(target: ConsoleLike, ring: ConsoleRing, now: () => string): () => void {
  const originals = LEVELS.map((level) => [level, target[level]] as const);
  for (const [level, original] of originals) {
    target[level] = (...args: unknown[]): void => {
      try {
        ring.push(`${now()} ${level} ${unstyled(args).map(wordsOf).join(" ")}`);
      } finally {
        original.apply(target, args);
      }
    };
  }
  return () => {
    for (const [level, original] of originals) {
      target[level] = original;
    }
  };
}
